import {NavigationTab} from '@/logged/features';
import {useNotifications} from '@/logged/features/notifications';
import {useUser} from '@/logged/features/user';
import {I18nService} from '@malt/vue3-i18n';
import {computed, Ref, ref} from 'vue';
import {CachedFilteredTab, useFilteredTabs} from '../useFilteredTabs';
import {NavigationTabsRecord} from './navigation.types';

export function useClientNavigationTabs(cachedNavigationRoutes: Ref<CachedFilteredTab[]>): NavigationTabsRecord {
    const userStore = useUser();
    const notificationsStore = useNotifications();

    // - Identities
    const isAdmin = computed<boolean>(() => !!userStore.user?.options.admin);
    const isPlatformAdmin = computed<boolean>(() => !!userStore.user?.options.platformAdmin);
    const maySeeMembers = computed<boolean>(() => !!userStore.user?.options.maySeeMembers);
    const hasAFreelanceProfile = computed<boolean>(() => !!userStore.user?.identities.some(({type}) => type === 'FREELANCE'));

    // - Sourcing Hub
    const isABTestCampaingSourcingHubActivated = ref(window.isAlternativeHubSourcing);

    // - Insights
    const isEligibleToInsights = computed<boolean>(
        () => !!userStore.selectedIdentity?.organizationId != null && !!userStore.user?.options.accessGrantedToInsights,
    );
    const insightsBaseUrl = computed(() => userStore.user?.insightsBaseUrl);
    const insightsResourceUrl = computed(() => encodeURIComponent('/api/front/organizations/' + userStore.selectedIdentity?.organizationId));

    // - Reduced tabs

    const logoNavigationRoute = computed(() => '/');

    const allNavigationRoutes = computed((): NavigationTab[] => [
        {
            id: 'Messages',
            title: I18nService.msg('navbar.aside.tabs.title.messages'),
            url: {href: '/messages'},
            icons: {
                mainIcon: 'header/inbox',
            },
            notificationsCount: notificationsStore.notificationsRecord?.messages,
            isActiveWhen: ({startWith}) => startWith('/messages'),
        },
        {
            id: 'MonitorActivities',
            title: I18nService.msg('navbar.aside.tabs.title.monitor.activities'),
            url: {href: '/dashboard/customer'},
            icons: {
                mainIcon: 'aside/dashboard',
            },
            isActiveWhen: ({startWith}) => startWith('/dashboard/customer'),
        },
        {
            id: 'FindFreelancer',
            title: I18nService.msg('navbar.aside.tabs.title.find.freelancer'),
            icons: {
                mainIcon: 'aside/find-free',
            },
            constraint: !isABTestCampaingSourcingHubActivated.value,
            children: [
                {
                    id: 'BrowseProfiles',
                    title: I18nService.msg('navbar.aside.tabs.title.browse.profiles'),
                    url: {href: '/s'},
                    isActiveWhen: ({startWith, exact}) =>
                        startWith('/s') || exact('/') || exact('/all-freelances') || startWith('/profile') || startWith('/home'),
                },
                {
                    id: 'PostJobOffer',
                    title: I18nService.msg('navbar.aside.tabs.title.post.job.offer'),
                    url: {
                        href: '/customer/searches/draft/dispatch?brief_origin=lateral-navbar-deposit_project',
                    },
                    isActiveWhen: ({startWith}) => startWith('/customer/searches/draft'),
                },
                {
                    id: 'HireFreelancers',
                    title: I18nService.msg('navbar.aside.tabs.title.hire.freelancers'),
                    url: {
                        href: '/client/freelancers',
                    },
                    isActiveWhen: ({startWith}) => startWith('/customer/freelancers') || startWith('/client/freelancers'),
                },
            ],
        },
        {
            id: 'FindFreelancer',
            title: I18nService.msg('navbar.aside.tabs.title.find.freelancer'),
            url: {href: '/project-client/find-freelancers'},
            icons: {
                mainIcon: 'aside/find-free',
            },
            constraint: isABTestCampaingSourcingHubActivated.value,
            isActiveWhen: ({startWith}) => startWith('/project-client/find-freelancers'),
        },
        {
            id: 'HireFreelancers',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.pool'),
            url: {
                href: '/client/freelancers',
            },
            icons: {
                mainIcon: 'aside/freelancer-pool',
            },
            constraint: isABTestCampaingSourcingHubActivated.value,
            isActiveWhen: ({startWith}) => startWith('/customer/freelancers') || startWith('/client/freelancers'),
        },
        {
            id: 'ManageJobs',
            title: I18nService.msg('navbar.aside.tabs.title.manage.jobs'),
            icons: {
                mainIcon: 'aside/manage-projects',
            },
            notificationsCount: notificationsStore.notificationsRecord?.projectActions,
            children: [
                {
                    id: 'FollowUpApplications',
                    title: I18nService.msg('navbar.aside.tabs.title.follow.up.applications'),
                    url: {href: '/customer/searches'},
                    notificationsCount: notificationsStore.notificationsRecord?.sourcingActivities,
                    isActiveWhen: ({startWith}) => startWith('/customer/searches') || startWith('/client/sourcing-projects'),
                },
                {
                    id: 'Applications',
                    title: I18nService.msg('navbar.aside.tabs.title.ongoing.jobs'),
                    url: {
                        href: '/project-client',
                    },
                    notificationsCount: notificationsStore.notificationsRecord?.projectActions,
                    isActiveWhen: ({startWith}) =>
                        startWith('/client/missions/') || startWith('/proposal/') || startWith('/mission/') || startWith('/project-client'),
                },
            ],
        },
        {
            id: 'Monitoring',
            title: '--',
            constraint: isAdmin.value,
            ghostChildren: [
                {
                    id: 'Accounting',
                    title: I18nService.msg('navbar.aside.tabs.title.accounting'),
                    url: {
                        href: isEligibleToInsights.value
                            ? `${insightsBaseUrl.value}/app/organizations/${insightsResourceUrl.value}/invoice`
                            : `/invoicing/company/${userStore.selectedIdentity?.companyId}`,
                        blank: isEligibleToInsights.value,
                    },
                    icons: {
                        mainIcon: 'aside/invoice',
                        altIcon: isEligibleToInsights.value ? 'misc/insights' : undefined,
                    },
                    isActiveWhen: ({matchRegXp}) => matchRegXp(new RegExp(/^\/client\/.+\/invoices/)) || matchRegXp(/^\/invoicing\/company\/.+/),
                },
                {
                    id: 'Reporting',
                    title: I18nService.msg('navbar.aside.tabs.title.reporting'),
                    url: {
                        href: isEligibleToInsights.value
                            ? `${insightsBaseUrl.value}/app/organizations/${insightsResourceUrl.value}/reporting`
                            : `/dashboard-client/${userStore.selectedIdentity?.companyId}/reports`,
                        blank: isEligibleToInsights.value,
                    },
                    icons: {
                        mainIcon: 'aside/analytics',
                        altIcon: isEligibleToInsights.value ? 'misc/insights' : undefined,
                    },
                    isActiveWhen: ({matchRegXp}) =>
                        matchRegXp(new RegExp(/^\/client\/.+\/reports/)) || matchRegXp(/^\/dashboard-client\/.+\/reports/),
                },
            ],
        },
        {
            id: 'Company',
            title: I18nService.msg('navbar.aside.tabs.title.company'),
            icons: {
                mainIcon: 'aside/company',
            },
            separator: 'top',
            children: [
                {
                    id: 'MyTeam',
                    title: I18nService.msg('navbar.aside.tabs.title.my.team'),
                    url: {href: `/client-settings/team`},
                    constraint: maySeeMembers.value,
                    isActiveWhen: ({startWith}) => startWith('/client-settings/team'),
                },
                {
                    id: 'CompanySettings',
                    title: I18nService.msg('navbar.aside.tabs.title.company-settings'),
                    url: {
                        href: `/client-settings`,
                    },
                    isActiveWhen: ({startWith, matchRegXp}) => matchRegXp(/\/client-settings\/(?!team\/?$).+$/),
                    notificationsCount: notificationsStore.notificationsRecord?.companySettings,
                },
            ],
        },
        {
            id: 'AccountSettings',
            title: I18nService.msg('navbar.aside.tabs.title.account.settings'),
            icons: {
                mainIcon: 'aside/settings',
            },
            url: {href: '/account'},
            isActiveWhen: ({startWith}) => startWith('/account'),
        },
    ]);

    const {filteredNavigationRoutes} = useFilteredTabs(cachedNavigationRoutes, allNavigationRoutes);

    return {
        logoNavigationRoute,
        allNavigationRoutes,
        filteredNavigationRoutes,
    };
}
